import Ajv from 'ajv'
import { CalculationsService } from './CalculationsService'
import { destructureWithOmit, recursiveConvertSnakeToCamelCase } from '@/utils'
import { CalculationsTypeGuardService } from './CalculationsTypeGuardService'
import { Calculation } from '../models/Calculation'
import { CalculationSchema } from '../models/CalculationSchema'
import { IQueueExecutionRequest } from '@/shared/Executions/models/IQueueExecutionRequest'
import { CdnSchemaJsonParsingResult } from '@/shared/JsonEditorSchema/models/CdnSchemaJsonParsingResult'
import { SchemaJsonValidationService } from '@/shared/JsonEditorSchema/services/SchemaJsonValidationService'

export class CalculationsJsonParsingResult extends CdnSchemaJsonParsingResult<Calculation, CalculationsJsonParsingInputType> {

    public sort?: Array<string>;
    public name: string = "";

    constructor(
        valid: boolean,
        parsedValue?: any,
        errors: Array<string> = []
    ) {
        super(valid, parsedValue, errors);
        if (valid && parsedValue) {
            if ('customCalculations' in parsedValue && parsedValue.customCalculations?.calculations) {
                this.parsedEntities = parsedValue.customCalculations.calculations;
                this.sort = parsedValue.customCalculations?.output?.sort;
                this.name = parsedValue?.name;
                this.inputType = 'full';
            } else if ('calculations' in parsedValue && Array.isArray(parsedValue.calculations)) {
                this.parsedEntities = parsedValue.calculations;
                this.sort = parsedValue?.output?.sort;
                this.name = parsedValue?.name;
                this.inputType = 'flat';
            } else if (Array.isArray(parsedValue)) {
                this.parsedEntities = parsedValue;
                this.sort = [];
                this.inputType = 'array';
            }
        }
    }

    public withParsedCalculations(calculations: Calculation[]): this {
        this.parsedEntities = calculations;
        return this;
    }
}

interface ICalculationArrayStructure {
    customCalculations: {
        output: {
            sort: Array<string>
        },
        calculations: Calculation[];
    }
}

interface IAdvancedAnalysisExecutionPartCalculationsStructure {
    output: {
        sort: Array<string>
    },
    calculations: Calculation[];
}

type ICalculationsArray = Calculation[];

export type CalculationsJsonParsingInputType = 'full' | 'flat' | 'array';

export class CalculationsJsonParsingService extends SchemaJsonValidationService<Calculation, CalculationsJsonParsingInputType, CalculationSchema> {

    static readonly injectableKey = 'CalculationsJsonParsingService';

    get injectableKey() {
        return (this.constructor as typeof CalculationsJsonParsingService).injectableKey;
    }

    //@ts-ignore
    public initializeValidators(defaultType?: string): void {
        //noop
    }

    private _fullCalculationsPayloadStructureSchemaValidator = new Ajv().compile<IQueueExecutionRequest>({
        type: 'object',
        properties: {
            parentExecutionId: {
                type: 'string'
            },
            name: {
                type: 'string'
            },
            customCalculations: {
                type: 'object',
                properties: {
                    output: {
                        type: 'object',
                        properties: {
                            sort: {
                                type: 'array',
                                items: {
                                    type: 'string'
                                }
                            }
                        },
                        required: ['sort'],
                        additionalProperties: true
                    },
                    calculations: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['output', 'calculations'],
                additionalProperties: true
            }
        },
        required: ['parentExecutionId', 'customCalculations'],
        additionalProperties: true
    });

    private _calculationArrayStructureSchemaValidator = new Ajv().compile<ICalculationArrayStructure>({
        type: 'object',
        properties: {
            customCalculations: {
                type: 'object',
                properties: {
                    output: {
                        type: 'object',
                        properties: {
                            sort: {
                                type: 'array',
                                items: {
                                    type: 'string'
                                }
                            }
                        },
                        required: ['sort'],
                        additionalProperties: true
                    },
                    calculations: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['calculations'],
                additionalProperties: true
            }
        },
        required: ['customCalculations'],
        additionalProperties: true
    });

    private _advancedAnalysisExecutionPartPayloadStructureSchemaValidator = new Ajv().compile<IAdvancedAnalysisExecutionPartCalculationsStructure>({
        type: 'object',
        properties: {
            output: {
                type: 'object',
                properties: {
                    sort: {
                        type: 'array',
                        items: {
                            type: 'string'
                        }
                    }
                },
                required: ['sort'],
                additionalProperties: true
            },
            calculations: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        type: { type: 'string' }
                    },
                    required: ['type'],
                    additionalProperties: true
                }
            }
        },
        required: ['calculations'],
        additionalProperties: true
    });

    private _calculationsArraySchemaValidator = new Ajv().compile<ICalculationsArray>({
        type: 'array',
        items: {
            type: 'object',
            properties: {
                type: { type: 'string' }
            },
            required: ['type'],
            additionalProperties: true
        }
    });

    public tryParseEntitiesFromJson(value: string, calculationSchemas: Array<CalculationSchema>): CalculationsJsonParsingResult {
        if (calculationSchemas == null || calculationSchemas.length <= 0) throw new Error("calculationSchemas is required");

        var result = new CalculationsJsonParsingResult(true);

        if (value == null) {
            result.valid = false;
            result.errors.push("Cannot parse JSON from null or empty input value.");
            return result;
        }

        try {
            result.parsedValue = JSON.parse(value);
            recursiveConvertSnakeToCamelCase(result.parsedValue)
            return this.tryParseCalculations(result, calculationSchemas);
        }
        catch (error: any) {
            result.valid = false;
            result.errors.push(error);
        }

        return result;
    }

    private tryParseCalculations(result: CalculationsJsonParsingResult, calculationSchemas: Array<CalculationSchema>): CalculationsJsonParsingResult {

        if (result.parsedValue == null) throw new Error("Invalid calculations.parseValue encountered trying to validateCalculationStructure.");

        const parsed = result.parsedValue;
        let calculations: Calculation[] | undefined;

        // reset errors from any prior validation
        this._fullCalculationsPayloadStructureSchemaValidator.errors = null;
        this._calculationArrayStructureSchemaValidator.errors = null;
        this._advancedAnalysisExecutionPartPayloadStructureSchemaValidator.errors = null;
        this._calculationsArraySchemaValidator.errors = null;

        if (this._fullCalculationsPayloadStructureSchemaValidator(parsed)) {
            calculations = parsed.customCalculations.calculations;
        } else if (this._calculationArrayStructureSchemaValidator(parsed)) {
            calculations = parsed.customCalculations.calculations;
        } else if (this._advancedAnalysisExecutionPartPayloadStructureSchemaValidator(parsed)) {
            calculations = parsed.calculations;
        } else if (this._calculationsArraySchemaValidator(parsed)) {
            calculations = parsed;
        } else {
            const errors = [
                ...(this._fullCalculationsPayloadStructureSchemaValidator.errors || []),
                ...(this._advancedAnalysisExecutionPartPayloadStructureSchemaValidator.errors || []),
                ...(this._calculationsArraySchemaValidator.errors || [])
            ]
                .map((e: any) => `${e.instancePath} ${e.message}`)
                .slice(0, 5);

            return new CalculationsJsonParsingResult(false, undefined, errors);
        }

        try {
            const initialized = calculations.map(x => this.initializeEntityWithConfiguration(x, calculationSchemas));
            return new CalculationsJsonParsingResult(true, parsed).withParsedCalculations(initialized);
        } catch (err: any) {
            return new CalculationsJsonParsingResult(false, parsed, [`${err.message}`]);
        }
    }

    public initializeEntityWithConfiguration(calculation: Calculation, calculationSchemas: Array<CalculationSchema>): Calculation {
        if (calculation == null) throw new Error("calculation is required");
        if (!calculation.type) throw new Error("calculation.type is required")
        return this.initializeEntity(calculation, calculationSchemas, 1);
    }

    public initializeEntity(calculation: Calculation, calculationSchemas: Array<CalculationSchema>, configurationLevel: number): Calculation {
        let initializedCalculation = calculation;
        const calculationSchema = CalculationsService.tryGetCalculationSchemaByType(calculation.type, calculationSchemas);
        if (calculationSchema != null) {
            initializedCalculation = CalculationsService.createCalculationFromCalculationSchema(calculationSchema);
            if (calculation.description != null) initializedCalculation.description = calculation.description;
            initializedCalculation._type = calculation.type;
            initializedCalculation.configuration = destructureWithOmit(calculation, []);
            initializedCalculation.configuration._type = calculation.type;

            // Special-case for calculation comprised of calculations or CalculationSchema with payload_template that may have groups 
            const calculationRequiresRecursiveInitialization = this.calculationRequiresRecursiveInitialization(calculation);
            const calculationSchemaRequiresRecursiveInitialization = this.calculationSchemaRequiresRecursiveInitialization(calculationSchema);

            if (configurationLevel > 1 &&
                (calculationRequiresRecursiveInitialization || calculationSchemaRequiresRecursiveInitialization)) {
                throw new Error("Non top-level calculations that contain calculations not supported.");
            }
            else if (calculationRequiresRecursiveInitialization) {
                throw new Error("Calculations containing child calculations is not supported.");
                /*
                const initializedSubCalculations = new Array<CustomCalculation>();
                (initializedCalculation.configuration?.calculations || []).forEach((c: CustomCalculation) => {
                    initializedSubCalculations.push(this.initializeCalculation(c, calculationSchemas, configurationLevel + 1))
                });
                initializedCalculation.configuration.calculations = initializedSubCalculations;
                */
            }

        }
        return initializedCalculation;
    }

    private calculationRequiresRecursiveInitialization(calculation: Calculation): boolean {
        return CalculationsTypeGuardService.supportsChildCalculations(calculation);
    }

    private calculationSchemaRequiresRecursiveInitialization(calculationSchema: CalculationSchema): boolean {
        return CalculationsTypeGuardService.supportsChildCalculations(calculationSchema);
    }
}
